/*
 * autolinks.js — transforma em link as palavras do texto que correspondem a itens do
 * menu principal. Os termos NÃO são uma lista curada: são o próprio menu, exportado
 * como JSON por `snippets/autolinks.liquid`. Item novo no menu vira termo linkável
 * sozinho — o que é a força e a fraqueza da ideia.
 *
 * ⚠️ ESTE ARQUIVO ERA MINIFICADO E AGORA É LEGÍVEL. Origem: `modules/autolinks.js` do
 * projeto do Gustavo (`app-gustavo-referencia/Casa Boa Vista`), 264 linhas, que gerava
 * o `assets/autolinks.js` comprimido via Gulp. Conferido em 13/08/2026: o comprimido
 * que rodava no site era byte a byte igual ao dele, ou seja, aquele fonte era mesmo a
 * origem do que estava no ar.
 *
 * POR QUE NÃO REGERAMOS PELO BUILD. O `package.json` daquele projeto não declara
 * nenhuma dependência — o gulpfile precisa de webpack, terser, babel e sass, e nada
 * disso está listado. Reconstruir exigiria adivinhar versões, e minificador de versão
 * diferente produz saída diferente: não daria para provar que só a nossa mudança
 * mudou. Editar direto em `assets/` já é exceção prevista nas regras do tema (é assim
 * com os `decimal-quantity*.js`), então o arquivo passou a viver aqui, legível.
 *
 * O tema divergiu do projeto do Gustavo, e este arquivo não volta mais para lá.
 */
class Autolinks {
  /**
   * Rede de segurança, usada SÓ se o bloco de configuração não existir na página —
   * tema antigo, snippet não atualizado. Com o snippet no lugar, quem manda é o
   * editor de tema.
   *
   * "sarja" é o caso que originou tudo: na frase "em armação sarja" a palavra descreve
   * a TRAMA, não o produto, e o link mandava a cliente de uma página de viscose
   * sarjada para `/collections/tecido-sarja`, que é outro tecido, com 7 produtos.
   * Medido em 13/08/2026 numa página de produto real.
   */
  static IGNORADOS_PADRAO = ['sarja'];

  constructor(selectors = []) {
    this.selectors = selectors;
    this.menuData = this.getMenuData();
    this.termosIgnorados = this.getTermosIgnorados();
    this.init();
  }

  /**
   * Termos excluídos, do editor de tema (SEO Template → Links automáticos).
   *
   * Fica em configuração, e não no código, porque quem descobre que um termo está
   * linkando errado é quem lê o site — e não deve precisar de publicação de tema para
   * corrigir. A comparação é com o título INTEIRO do item de menu, então excluir
   * "Sarja" não afeta "Viscose Sarjada".
   */
  getTermosIgnorados() {
    let lista = Autolinks.IGNORADOS_PADRAO;
    try {
      const el = document.getElementById('autolinks-ignorados');
      if (el) lista = JSON.parse(el.textContent);
    } catch (e) {
      /* configuração ilegível cai no padrão em vez de deixar tudo passar */
    }
    return new Set(
      (lista || [])
        .map((t) => String(t).trim().toLowerCase())
        .filter(Boolean),
    );
  }

  /** Lê o JSON do menu que o Liquid injetou na página. */
  getMenuData() {
    try {
      const el = document.getElementById('autolinks-menu-data');
      if (el) return JSON.parse(el.textContent);
    } catch (e) {
      /* menu ilegível não pode derrubar a página — sem termos, o texto fica como está */
    }
    return [];
  }

  init() {
    if (this.menuData.length === 0) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.processSelectors());
    } else {
      this.processSelectors();
    }
  }

  processSelectors() {
    this.selectors.forEach((selector) => this.processSelector(selector));
  }

  processSelector(selector) {
    try {
      document.querySelectorAll(selector).forEach((el) => this.processElement(el));
    } catch (e) {
      /* seletor inválido não derruba os outros */
    }
  }

  /**
   * Menu ordenado para casamento: frases compostas primeiro, e dentro de cada grupo do
   * título mais longo para o mais curto.
   *
   * A ordem é o que faz "Viscose Sarjada" ganhar de "Viscose" — sem ela, o termo curto
   * casaria primeiro e o composto nunca seria usado.
   */
  organizeMenuWithComposites() {
    if (this.organizedMenuCache) return this.organizedMenuCache;

    const compostas = [];
    this.menuData.forEach((item) => {
      // Submenu e terceiro nível viram "Pai Filho" — ex.: "Linho" + "Puro" = "Linho Puro"
      if ((item.level === 'submenu' || item.level === 'level3') && item.parent) {
        compostas.push({
          title: `${item.parent} ${item.title}`,
          url: item.url,
          handle: item.handle,
          level: 'composite',
          parent: item.parent,
          originalSubItem: item.title,
        });
      }
    });

    const porTamanho = (a, b) => b.title.length - a.title.length;
    this.organizedMenuCache = [
      ...compostas.sort(porTamanho),
      ...[...this.menuData].sort(porTamanho),
    ];
    return this.organizedMenuCache;
  }

  /**
   * A posição da primeira ocorrência do termo que NÃO está dentro de um link, ou -1.
   *
   * A checagem de "está dentro de um <a>" é por posição da última tag aberta contra a
   * última fechada antes do ponto. É grosseira, mas o HTML aqui é descrição de produto
   * — parágrafos, negrito e links —, não documento arbitrário.
   */
  primeiraOcorrenciaLivre(html, regex) {
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(html)) !== null) {
      const antes = html.substring(0, match.index);
      // Dentro de uma tag (atributo alt, title...) não é texto: inserir o link ali quebra
      // a tag e o resto dela aparece escrito na página. Aconteceu em 14/10/2026 no post
      // do linho, com "linho estampado" no alt de uma imagem.
      if (antes.lastIndexOf('<') > antes.lastIndexOf('>')) continue;
      const abriu = antes.lastIndexOf('<a');
      const fechou = antes.lastIndexOf('</a>');
      if (abriu === -1 || fechou > abriu) return match.index;
    }
    return -1;
  }

  processElement(element) {
    if (!element || element.hasAttribute('data-autolinks-processed')) return;
    element.setAttribute('data-autolinks-processed', 'true');

    let html = element.innerHTML;
    let modificado = false;

    this.organizeMenuWithComposites().forEach((menuItem) => {
      if (!menuItem.handle) return;
      if (this.termosIgnorados.has(menuItem.title.trim().toLowerCase())) return;

      // Um handle já linkado não é processado de novo — evita que o mesmo destino
      // apareça duas vezes por caminhos diferentes (item simples e composto).
      if (html.includes(`data-autolink-handle="${menuItem.handle}"`)) return;

      const regex = new RegExp(`\\b${this.escapeRegex(menuItem.title)}\\b`, 'gi');
      const posicao = this.primeiraOcorrenciaLivre(html, regex);
      if (posicao === -1) return;

      /*
       * ⚠️ SÓ A PRIMEIRA OCORRÊNCIA, e isto é uma correção de 13/08/2026.
       *
       * A versão original substituía TODAS (`html.replace` com regex global). Numa
       * descrição de produto real isso produziu nove links, sendo três para a mesma
       * coleção. O Google considera essencialmente o primeiro link para uma URL numa
       * página, então repetição não acrescenta nada de SEO — e cada link a mais é uma
       * porta de saída numa página cuja função é vender, inclusive no meio do
       * parágrafo de cuidados, lido por quem já decidiu comprar.
       *
       * Recortar por posição, em vez de `replace`, também elimina o risco de o padrão
       * casar de novo dentro do link que acabou de ser inserido.
       */
      const encontrado = html.substr(posicao, menuItem.title.length);
      const link = `<a href="${menuItem.url}" class="autolink" data-autolink-handle="${menuItem.handle}">${encontrado}</a>`;
      html = html.slice(0, posicao) + link + html.slice(posicao + menuItem.title.length);
      modificado = true;
    });

    if (modificado) element.innerHTML = html;
  }

  escapeRegex(texto) {
    return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  getSelectors() {
    return [...this.selectors];
  }
}

window.Autolinks = Autolinks;

document.addEventListener('DOMContentLoaded', () => {
  window.autolinksInstance = new Autolinks([
    '.description-tabs-block .description-tab-item:first-child .description-tab-answer-content',
    '.section-product-banner .product-banner-content .product-banner-description',
    '.faq-answer-content',
    '.collection-header-description',
    '.blog-post-excerpt',
    '.article-body',
    '.page-content',
  ]);
});
